import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Brain, LineChart, Users, Sparkles } from 'lucide-react';
import OnboardingComplete from '../components/OnboardingComplete';
import { getTranslation, SUPPORTED_LANGUAGES } from '../translations';

// Google icon SVG component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export default function Landing({ onAuth }) {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', surname: '' });
  const [loading, setLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const t = (key) => getTranslation(key, selectedLanguage);

  const handleForgotPassword = async () => {
    if (!formData.email) { toast.error('Please enter your email first'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: formData.email });
      setForgotSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    // Redirect to root with hash, not dashboard - this preserves the session_id hash in callback
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLogin) {
      // Direct login
      setLoading(true);
      try {
        const res = await axios.post(`${API}/auth/login`, formData);
        onAuth(res.data.token, res.data.user);
        toast.success(t('welcome_back'));
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Authentication failed');
      } finally {
        setLoading(false);
      }
    } else {
      // Check if email already exists BEFORE starting onboarding
      setLoading(true);
      try {
        const check = await axios.post(`${API}/auth/check-email`, { email: formData.email });
        if (check.data.exists) {
          setEmailExists(true);
          setIsLogin(true);
          setLoading(false);
          return;
        }
      } catch {
        // If check fails, proceed with onboarding anyway — signup will catch duplicates
      }
      setLoading(false);
      setShowOnboarding(true);
    }
  };

  const handleOnboardingComplete = async (preferences) => {
    setLoading(true);
    try {
      const signupData = {
        ...formData,
        gender: preferences.gender,
        phone: preferences.phone,
        country_code: preferences.country_code,
        city: preferences.city,
        country: preferences.country,
        area: preferences.area,
        language: preferences.language,
        music_preferences: preferences.music_preferences || [],
        food_preferences: preferences.food_preferences || [],
        wellness_goals: preferences.wellness_goals || [],
        email_verified: preferences.email_verified || false,
        phone_verified: preferences.phone_verified || false
      };
      const res = await axios.post(`${API}/auth/signup`, signupData);
      sessionStorage.setItem('mojo_just_signed_up', 'true');
      onAuth(res.data.token, res.data.user);
      toast.success('Welcome to MyMojo!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Signup failed';
      if (msg.includes('already registered')) {
        setShowOnboarding(false);
        setEmailExists(true);
        setIsLogin(true);
      } else {
        toast.error(msg);
        setShowOnboarding(false);
      }
    } finally {
      setLoading(false);
    }
  };
  if (showOnboarding) {
    return (
      <OnboardingComplete 
        onComplete={handleOnboardingComplete} 
        userEmail={formData.email}
        userName={formData.name}
        userSurname={formData.surname}
        initialLanguage={selectedLanguage}
        onEmailExists={() => {
          setShowOnboarding(false);
          setEmailExists(true);
          setIsLogin(true);
        }}
      />
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-b from-white via-stone-50 to-stone-100 overflow-hidden">
      <div className="flex-1 flex items-center justify-center px-4 py-4">
        <div className="grid lg:grid-cols-2 gap-6 items-center max-w-6xl w-full">
          {/* Left side - Features (Hidden on mobile for space) */}
          <div className="hidden lg:block space-y-6 max-w-xl">
            <div className="space-y-3">
              <h1 
                className="text-3xl xl:text-4xl font-semibold tracking-tight text-warm-charcoal leading-tight"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Understand Your Emotional Landscape
              </h1>
              <p className="text-base text-warm-charcoal/70 leading-relaxed font-light">
                Track emotional patterns, gain insights, and cultivate emotional intelligence.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-hunter-green/10 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-hunter-green" />
                </div>
                <h3 className="font-semibold text-sm">Multi-Dimensional</h3>
                <p className="text-xs text-warm-charcoal/60">Track emotions in depth</p>
              </div>

              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-sage/20 flex items-center justify-center">
                  <LineChart className="w-5 h-5 text-hunter-green" />
                </div>
                <h3 className="font-semibold text-sm">Pattern Recognition</h3>
                <p className="text-xs text-warm-charcoal/60">Discover trends</p>
              </div>

              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-sandstone/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-hunter-green" />
                </div>
                <h3 className="font-semibold text-sm">Community</h3>
                <p className="text-xs text-warm-charcoal/60">Connect anonymously</p>
              </div>

              <div className="space-y-2">
                <div className="w-10 h-10 rounded-xl bg-hunter-green/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-hunter-green" />
                </div>
                <h3 className="font-semibold text-sm">AI Companion</h3>
                <p className="text-xs text-warm-charcoal/60">Evidence-based support</p>
              </div>
            </div>
          </div>

          {/* Right side - Auth Form (Full width on mobile) */}
          <div className="zen-card p-6 md:p-8 max-w-md mx-auto w-full">
            {/* Mobile-only title */}
            <div className="lg:hidden mb-4 text-center">
              <h1 className="text-2xl font-semibold text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>
                Mind My Mojo
              </h1>
              <p className="text-sm text-warm-charcoal/60 mt-1">Emotional Wellness Tracker</p>
            </div>
            
            <h2 className="text-xl md:text-2xl font-semibold mb-5 text-center" style={{ fontFamily: 'Playfair Display, serif', color: '#1C1917' }}>
              {isLogin ? 'Welcome Back' : 'Begin Your Journey'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="name" className="text-xs font-medium text-warm-charcoal/70">First Name</Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required={!isLogin}
                      placeholder="John"
                      className="rounded-lg h-10 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="surname" className="text-xs font-medium text-warm-charcoal/70">Surname</Label>
                    <Input
                      id="surname"
                      type="text"
                      value={formData.surname}
                      onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                      required={!isLogin}
                      placeholder="Doe"
                      className="rounded-lg h-10 text-sm"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <Label htmlFor="email" className="text-xs font-medium text-warm-charcoal/70">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="john@example.com"
                  className="rounded-lg h-10 text-sm"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="password" className="text-xs font-medium text-warm-charcoal/70">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  placeholder="••••••••"
                  className="rounded-lg h-10 text-sm"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full zen-button bg-hunter-green text-white hover:bg-hunter-green/90 h-11 text-sm mt-2"
              >
                {loading ? 'Please wait...' : (isLogin ? 'Log In' : 'Create Account')}
              </Button>

              {/* Forgot Password link — shown on login */}
              {isLogin && (
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="w-full text-xs text-hunter-green hover:underline mt-2"
                  data-testid="forgot-password-btn"
                  style={{ touchAction: 'manipulation' }}
                >
                  Forgot Password?
                </button>
              )}
            </form>

            {/* Email already exists banner */}
            {emailExists && (
              <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                <p className="text-xs text-amber-900 font-medium mb-1">This email is already registered</p>
                <p className="text-[11px] text-amber-800/70">Please log in with your password, or tap "Forgot Password?" to reset it.</p>
              </div>
            )}

            {/* Forgot password sent confirmation */}
            {forgotSent && (
              <div className="mt-3 p-3 rounded-xl bg-green-50 border border-green-200">
                <p className="text-xs text-green-900 font-medium mb-1">Reset link sent!</p>
                <p className="text-[11px] text-green-800/70">Check your email (and spam folder) for a password reset link from MindMyMojo.</p>
              </div>
            )}

            <div className="mt-4 text-center">
              <p className="text-xs text-warm-charcoal/50 mb-2">
                {isLogin ? "Don't have an account?" : "Already have an account?"}
              </p>
              <Button
                onClick={() => { setIsLogin(!isLogin); setEmailExists(false); setForgotSent(false); }}
                variant="outline"
                className="w-full border-2 border-hunter-green text-hunter-green hover:bg-hunter-green hover:text-white transition-all h-11 text-sm font-medium"
              >
                {isLogin ? "Create Account" : "Log In"}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Footer link */}
      <div className="py-3 text-center">
        <button onClick={() => window.location.href = '/about'} className="text-xs text-warm-charcoal/40 hover:text-hunter-green transition-colors underline" data-testid="landing-about-link">
          About Mind My Mojo
        </button>
      </div>
    </div>
  );
}